import React, { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { finalize } from 'rxjs/operators';
import { RegionAPI } from '@business/api/region';
import { DealResponse } from '@business/entities/deal';
import { DealRegionStatus } from '@business/entities/region';
import { handleMessage, requestMessage } from '@business/messages';
import { UserService } from '@business/services';
import { isRegionsAccount } from '@business/services/common_service/common_service';
import { Session } from '@modules/auth/session';
import { TextBlock } from '@modules/common/TextBlock';
import { openSetDealTerritoryModal } from './SetDealTerritoryModal';

const EXCEPTION_TEXT: Partial<Record<DealRegionStatus, string>> = {
  overlap: 'Inside more than one territory',
  no_match: 'No territory covers this address',
  not_located: "The address couldn't be found on the map",
  no_address: 'No address on file',
};

export interface DealTerritoryProps {
  deal: DealResponse;
  onChanged(): void;
}

/**
 * The territory a deal's address falls in, its eligible representatives and
 * the one assigned to the deal's appointment. Overlaps and misses are shown
 * as they are for an admin to settle by hand.
 */
export const DealTerritory = memo(({ deal, onChanged }: DealTerritoryProps) => {
  const canManage = useMemo(() => !!Session.user && UserService.isAdmin(Session.user), []);
  const [checking, setChecking] = useState(false);

  const recheck = useCallback(() => {
    setChecking(true);
    RegionAPI.matchDealRegion(deal.id)
      .pipe(
        handleMessage({ type: requestMessage('match_deal_region') }),
        finalize(() => setChecking(false)),
      )
      .subscribe(() => onChanged());
  }, [deal.id, onChanged]);

  const choose = useCallback(
    () =>
      openSetDealTerritoryModal({ dealId: deal.id, regionId: deal.regionId ?? null, candidates: deal.regionCandidates || [], onSaved: onChanged }),
    [deal.id, deal.regionCandidates, deal.regionId, onChanged],
  );

  if (!isRegionsAccount()) return null;

  const status = deal.regionStatus ?? null;
  const reps = deal.region?.salesReps || [];

  let territory: ReactNode = <span className="text-muted">Not checked yet</span>;
  if (deal.region) {
    territory = (
      <>
        {deal.region.name}
        {status === 'manual' && <span className="text-muted font-size-11 ml-1">(set manually)</span>}
      </>
    );
  } else if (deal.deletedRegion) {
    territory = (
      <span className="text-warning">
        <i className="bx bx-error-circle mr-1" />
        {deal.deletedRegion.name} was deleted
      </span>
    );
  } else if (status === 'manual') {
    territory = <span className="text-muted">None (set manually)</span>;
  } else if (status && EXCEPTION_TEXT[status]) {
    const names = (deal.regionCandidates || []).map(c => c.name).join(', ');
    territory = (
      <span className="text-warning">
        <i className="bx bx-error-circle mr-1" />
        {EXCEPTION_TEXT[status]}
        {status === 'overlap' && names ? `: ${names}` : ''}
      </span>
    );
  }

  return (
    <div className="d-flex flex-wrap mb-4 project-line">
      <TextBlock
        title={
          <>
            Territory
            {canManage && (
              <>
                <a className="ml-2" title="Check the address against the territories again" onClick={checking ? undefined : recheck}>
                  <i className={`bx ${checking ? 'bx-loader bx-spin' : 'bx-revision'} font-size-16`} />
                </a>
                <a className="ml-1" title="Choose the territory" onClick={choose}>
                  <i className="bx bx-edit font-size-16" />
                </a>
              </>
            )}
          </>
        }
      >
        {territory}
      </TextBlock>
      <TextBlock title="Territory Representatives">
        {reps.length ? reps.map(rep => `${rep.firstName} ${rep.lastName}`).join(', ') : <span className="text-muted">—</span>}
      </TextBlock>
      <TextBlock title="Address Used">{deal.regionAddress || <span className="text-muted">—</span>}</TextBlock>
      <TextBlock title="Representative">{deal.representative?.name || <span className="text-muted">Not assigned</span>}</TextBlock>
      <div style={{ flex: 1 }} />
    </div>
  );
});
